import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Paper, CircularProgress } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import StraightenIcon from '@mui/icons-material/Straighten';
import PlaceIcon from '@mui/icons-material/Place';
import { locationService } from '../../services/locationService';
import { smartWarehouseService } from '../../services/smartWarehouseService';
import {
  aStarPath,
  getAllAisleWalkableCells,
  entryCenterX,
  entryCenterY,
} from '../../components/map/mapTypes';
import type {
  LocationMapItem,
  EntryExitItem,
  ObstacleItem,
  RoutePoint,
} from '../../components/map/mapTypes';
import type { PickListItem } from './PickingActiveFlow';

interface PickerNavMapProps {
  warehouseId: string;
  pickList: PickListItem[];
  currentIndex: number;
  /** Lokasyon merkezini izlemek için. */
  height?: number | string;
  /** Harita üzerinde gösterilecek okunabilir konum özeti (ör. "Rack 08 · Aisle 1"). */
  locationSubtitle?: string;
  /** Haritanın altına eklenecek içerik (ör. lokasyon barkod okutma). */
  footer?: React.ReactNode;
  /** Seçili olmayan alternatif rotanın durakları (kesikli ghost çizgi). */
  ghostStops?: { gridX: number; gridY: number }[];
}

const COLOR_GHOST = '#546e7a';

interface MapData {
  locations: LocationMapItem[];
  entryExits: EntryExitItem[];
  obstacles: ObstacleItem[];
  warehouseWidth: number;
  warehouseHeight: number;
}

const COLOR_PICKED = '#43a047';
const COLOR_TARGET = '#fb8c00';
const COLOR_REMAIN = '#90a4ae';
const COLOR_OTHER  = '#cfd8dc';
const COLOR_AISLE  = '#fafafa';
const COLOR_PLATFORM = '#e0e0e0';
const COLOR_ZONE_BG = 'rgba(33, 150, 243, 0.05)';
const COLOR_OBSTACLE = '#616161';
const COLOR_ROUTE = '#1565c0';
const BG = '#f7fafc';

function flattenTreeToLocations(nodes: any[], parentId: string | null = null): LocationMapItem[] {
  const out: LocationMapItem[] = [];
  for (const n of nodes || []) {
    const id = n.Id || n.id;
    const type = (n.Type || n.type) as LocationMapItem['type'];
    if (type !== 'Zone' && type !== 'Aisle' && type !== 'Platform' && type !== 'Rack' && type !== 'Level' && type !== 'Bin') {
      const children = n.Children || n.children || [];
      if (children.length) out.push(...flattenTreeToLocations(children, id ?? parentId));
      continue;
    }
    out.push({
      id,
      type,
      code: n.Code || n.code || '',
      name: n.Name || n.name || '',
      gridX: n.GridX ?? n.gridX ?? 0,
      gridY: n.GridY ?? n.gridY ?? 0,
      gridWidth: n.GridWidth ?? n.gridWidth ?? (type === 'Rack' ? 1 : type === 'Zone' ? 8 : type === 'Aisle' ? 2 : 3),
      gridHeight: n.GridHeight ?? n.gridHeight ?? (type === 'Rack' ? 1 : type === 'Zone' ? 6 : 10),
      parentId: n.ParentId ?? n.parentId ?? parentId,
      aisleId: n.AisleId ?? n.aisleId ?? null,
      aisleSide: n.AisleSide ?? n.aisleSide ?? null,
      sortOrder: n.SortOrder ?? n.sortOrder ?? 0,
      isPickable: n.IsPickable ?? n.isPickable ?? (type === 'Rack'),
      isNew: false,
      isModified: false,
      isDeleted: false,
    });
    const children = n.Children || n.children || [];
    if (children.length) out.push(...flattenTreeToLocations(children, id ?? parentId));
  }
  return out;
}

function mapFlatLocations(rows: any[]): LocationMapItem[] {
  return (rows || [])
    .map((n: any) => {
      const type = (n.Type || n.type) as LocationMapItem['type'];
      if (type !== 'Zone' && type !== 'Aisle' && type !== 'Platform' && type !== 'Rack' && type !== 'Level' && type !== 'Bin') {
        return null;
      }
      return {
        id: n.Id || n.id,
        type,
        code: n.Code || n.code || '',
        name: n.Name || n.name || '',
        gridX: n.GridX ?? n.gridX ?? 0,
        gridY: n.GridY ?? n.gridY ?? 0,
        gridWidth: n.GridWidth ?? n.gridWidth ?? (type === 'Rack' ? 1 : type === 'Zone' ? 8 : type === 'Aisle' ? 2 : 3),
        gridHeight: n.GridHeight ?? n.gridHeight ?? (type === 'Rack' ? 1 : type === 'Zone' ? 6 : 10),
        parentId: n.ParentId ?? n.parentId ?? null,
        aisleId: n.AisleId ?? n.aisleId ?? null,
        aisleSide: n.AisleSide ?? n.aisleSide ?? null,
        sortOrder: n.SortOrder ?? n.sortOrder ?? 0,
        isPickable: n.IsPickable ?? n.isPickable ?? (type === 'Rack'),
        isNew: false,
        isModified: false,
        isDeleted: false,
      } as LocationMapItem;
    })
    .filter((x: any): x is LocationMapItem => !!x);
}

function mapEntryExits(nodes: any[]): EntryExitItem[] {
  return (nodes || [])
    .filter((n: any) => {
      const nt = n.NodeType || n.nodeType || '';
      return nt === 'Entry' || nt === 'Exit';
    })
    .map((n: any) => ({
      id: n.Id || n.id,
      nodeType: (n.NodeType || n.nodeType) as 'Entry' | 'Exit',
      name: n.Name || n.name || '',
      gridX: n.GridX ?? n.gridX ?? 0,
      gridY: n.GridY ?? n.gridY ?? 0,
      isNew: false,
      isModified: false,
      isDeleted: false,
    }));
}

function mapObstacles(rows: any[]): ObstacleItem[] {
  return (rows || []).map((n: any) => ({
    id: n.Id || n.id,
    obstacleType: (n.ObstacleType || n.obstacleType || 'Other') as ObstacleItem['obstacleType'],
    gridX: n.GridX ?? n.gridX ?? 0,
    gridY: n.GridY ?? n.gridY ?? 0,
    gridWidth: n.GridWidth ?? n.gridWidth ?? 1,
    gridHeight: n.GridHeight ?? n.gridHeight ?? 1,
    isNew: false,
    isModified: false,
    isDeleted: false,
  }));
}

const PickerNavMap: React.FC<PickerNavMapProps> = ({
  warehouseId,
  pickList,
  currentIndex,
  height = 320,
  locationSubtitle,
  footer,
  ghostStops = [],
}) => {
  const { t } = useTranslation('warehouse');
  const [data, setData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Layout ölçüsü için ResizeObserver
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Veriyi yükle
  useEffect(() => {
    if (!warehouseId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Try tree first (richer, includes children); fallback to flat list.
        let locations: LocationMapItem[] = [];
        try {
          const treeRes: any = await locationService.getTree(warehouseId);
          const treeData = treeRes?.data?.data || treeRes?.data?.Data || treeRes?.data || [];
          locations = flattenTreeToLocations(treeData);
        } catch {
          const flatRes: any = await locationService.getByWarehouse(warehouseId);
          const flatData = flatRes?.data?.data || flatRes?.data?.Data || flatRes?.data || [];
          locations = mapFlatLocations(flatData);
        }

        let entryExits: EntryExitItem[] = [];
        try {
          const eeRes: any = await smartWarehouseService.getEntryExits(warehouseId);
          const eeData = eeRes?.data?.data || eeRes?.data?.Data || eeRes?.data || [];
          entryExits = mapEntryExits(eeData);
        } catch {
          entryExits = [];
        }

        let obstacles: ObstacleItem[] = [];
        try {
          const obsRes: any = await locationService.getObstacles(warehouseId);
          const obsData = obsRes?.data?.data || obsRes?.data?.Data || obsRes?.data || [];
          obstacles = mapObstacles(obsData);
        } catch {
          obstacles = [];
        }

        // Warehouse boyutu: bilinen item'lerden max al, fallback 40x30.
        let maxX = 0, maxY = 0;
        for (const l of locations) {
          maxX = Math.max(maxX, l.gridX + (l.gridWidth || 1));
          maxY = Math.max(maxY, l.gridY + (l.gridHeight || 1));
        }
        for (const ee of entryExits) {
          maxX = Math.max(maxX, ee.gridX + 2);
          maxY = Math.max(maxY, ee.gridY + 2);
        }
        for (const ob of obstacles) {
          maxX = Math.max(maxX, ob.gridX + (ob.gridWidth || 1));
          maxY = Math.max(maxY, ob.gridY + (ob.gridHeight || 1));
        }
        const warehouseWidth = Math.max(20, maxX);
        const warehouseHeight = Math.max(15, maxY);

        if (cancelled) return;
        setData({ locations, entryExits, obstacles, warehouseWidth, warehouseHeight });
      } catch (err: any) {
        if (!cancelled) setError(err?.message || t('screens.picking.navMap.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId, t]);

  // Auto-fit hesabı
  const cellPx = useMemo(() => {
    if (!data || containerSize.w <= 0 || containerSize.h <= 0) return 14;
    // Overlay yok; harita alanı tamamen çizim için kullanılır.
    const padding = 8;
    const sx = (containerSize.w - padding * 2) / data.warehouseWidth;
    const sy = (containerSize.h - padding * 2) / data.warehouseHeight;
    return Math.max(6, Math.min(sx, sy));
  }, [data, containerSize]);

  // Hedef + önceki konum
  const targetItem = pickList[currentIndex];
  const lastPicked = useMemo(() => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const it = pickList[i];
      if ((it?.status === 'Picked' || it?.status === 'ShortPick') && it.gridX != null && it.gridY != null) {
        return it;
      }
    }
    return null;
  }, [pickList, currentIndex]);

  // Toplama biten / kalan / hedef raf id setleri
  const { pickedIds, remainIds } = useMemo(() => {
    const picked = new Set<string>();
    const remain = new Set<string>();
    pickList.forEach((it, idx) => {
      if (!it.locationId) return;
      if (it.status === 'Picked' || it.status === 'ShortPick') {
        picked.add(it.locationId);
      } else if (idx !== currentIndex) {
        remain.add(it.locationId);
      }
    });
    return { pickedIds: picked, remainIds: remain };
  }, [pickList, currentIndex]);

  // Rota hesapla (start = lastPicked or entry, end = current target)
  const route = useMemo<RoutePoint[]>(() => {
    if (!data || !targetItem || targetItem.gridX == null || targetItem.gridY == null) return [];
    const walkable = getAllAisleWalkableCells(data.locations);

    // Start point: önceki toplanan rafın merkezi, yoksa giriş, yoksa hedefin yanı.
    let startPoint: RoutePoint;
    if (lastPicked && lastPicked.gridX != null && lastPicked.gridY != null) {
      startPoint = { gridX: lastPicked.gridX, gridY: lastPicked.gridY };
    } else {
      const entry = data.entryExits.find(e => e.nodeType === 'Entry') || data.entryExits[0];
      if (entry) {
        startPoint = { gridX: Math.floor(entryCenterX(entry)), gridY: Math.floor(entryCenterY(entry)) };
      } else {
        startPoint = { gridX: Math.max(0, targetItem.gridX - 1), gridY: targetItem.gridY };
      }
    }

    const endPoint: RoutePoint = { gridX: targetItem.gridX, gridY: targetItem.gridY };

    // Walkable üzerinden A*: rafın hücresi koridor değildir, yakın koridor hücresine snap
    const snapToNearestWalkable = (p: RoutePoint): RoutePoint => {
      const k = (x: number, y: number) => `${x},${y}`;
      if (walkable.has(k(Math.round(p.gridX), Math.round(p.gridY)))) return p;
      let best: RoutePoint | null = null;
      let bestD = Number.POSITIVE_INFINITY;
      walkable.forEach(s => {
        const [sxStr, syStr] = s.split(',');
        const sx = Number(sxStr), sy = Number(syStr);
        const d = Math.abs(sx - p.gridX) + Math.abs(sy - p.gridY);
        if (d < bestD) { bestD = d; best = { gridX: sx, gridY: sy }; }
      });
      return best || p;
    };

    const aStart = snapToNearestWalkable(startPoint);
    const aEnd = snapToNearestWalkable(endPoint);
    const path = aStarPath(aStart, aEnd, data.obstacles, {
      width: data.warehouseWidth,
      height: data.warehouseHeight,
    }, walkable);

    // Rafa son adım olarak gerçek hedef hücresini ekle (çizimde ok rafa ulaşsın)
    if (path.length > 0) {
      const last = path[path.length - 1];
      if (last.gridX !== endPoint.gridX || last.gridY !== endPoint.gridY) {
        path.push(endPoint);
      }
    }
    return path;
  }, [data, targetItem, lastPicked]);

  const distance = useMemo(() => {
    if (route.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < route.length; i++) {
      d += Math.abs(route[i].gridX - route[i - 1].gridX) + Math.abs(route[i].gridY - route[i - 1].gridY);
    }
    return d;
  }, [route]);

  // Alternatif rota ghost çizgisi: giriş → alternatif duraklar (A* ile).
  const ghostRoute = useMemo<RoutePoint[]>(() => {
    if (!data || ghostStops.length < 2) return [];
    const walkable = getAllAisleWalkableCells(data.locations);
    const snapToNearestWalkable = (p: RoutePoint): RoutePoint => {
      const k = (x: number, y: number) => `${x},${y}`;
      if (walkable.has(k(Math.round(p.gridX), Math.round(p.gridY)))) return p;
      let best: RoutePoint | null = null;
      let bestD = Number.POSITIVE_INFINITY;
      walkable.forEach(s => {
        const [sxStr, syStr] = s.split(',');
        const sx = Number(sxStr), sy = Number(syStr);
        const d = Math.abs(sx - p.gridX) + Math.abs(sy - p.gridY);
        if (d < bestD) { bestD = d; best = { gridX: sx, gridY: sy }; }
      });
      return best || p;
    };

    const waypoints: RoutePoint[] = [];
    const entry = data.entryExits.find(e => e.nodeType === 'Entry') || data.entryExits[0];
    if (entry) {
      waypoints.push({ gridX: Math.floor(entryCenterX(entry)), gridY: Math.floor(entryCenterY(entry)) });
    }
    for (const s of ghostStops) {
      waypoints.push({ gridX: s.gridX, gridY: s.gridY });
    }

    const full: RoutePoint[] = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      const a = snapToNearestWalkable(waypoints[i]);
      const b = snapToNearestWalkable(waypoints[i + 1]);
      const seg = aStarPath(a, b, data.obstacles, {
        width: data.warehouseWidth,
        height: data.warehouseHeight,
      }, walkable);
      if (seg.length === 0) {
        if (full.length === 0) full.push(a);
        full.push(b);
        continue;
      }
      if (full.length > 0) full.push(...seg.slice(1));
      else full.push(...seg);
    }
    return full;
  }, [data, ghostStops]);

  // Hedef raf bilgisi
  const targetLocationItem = useMemo(() => {
    if (!data || !targetItem?.locationId) return null;
    return data.locations.find(l => l.id === targetItem.locationId) || null;
  }, [data, targetItem]);

  if (!warehouseId) return null;

  const targetCode = targetLocationItem?.code || targetItem?.locationCode || t('screens.picking.navMap.noTarget');
  const subtitle = locationSubtitle
    || [targetItem?.locationName, targetItem?.aisleName].filter(Boolean).join(' · ');

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Konum bilgisi — haritanın dışında, üst şerit */}
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Chip
          icon={<PlaceIcon sx={{ color: '#fff !important', fontSize: '16px !important' }} />}
          label={targetCode}
          size="small"
          sx={{
            bgcolor: COLOR_TARGET,
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.8rem',
            height: 28,
            letterSpacing: 0.2,
            '& .MuiChip-icon': { color: '#fff', ml: 0.5 },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          icon={<StraightenIcon sx={{ fontSize: '14px !important' }} />}
          label={distance > 0 ? t('screens.picking.navMap.steps', { count: distance }) : '—'}
          size="small"
          variant="outlined"
          sx={{ height: 26, fontSize: '0.7rem' }}
        />
        {subtitle ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: 12,
              lineHeight: 1.2,
              wordBreak: 'break-word',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        bgcolor: BG,
        overflow: 'hidden',
        flexShrink: 0,
      }}
      ref={wrapperRef}
    >
      {/* İçerik */}
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && data && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: data.warehouseWidth * cellPx,
            height: data.warehouseHeight * cellPx,
          }}
        >
          {/* Zone'lar */}
          {data.locations
            .filter(l => l.type === 'Zone')
            .map(z => (
              <Box key={z.id} sx={{
                position: 'absolute',
                left: z.gridX * cellPx, top: z.gridY * cellPx,
                width: z.gridWidth * cellPx, height: z.gridHeight * cellPx,
                bgcolor: COLOR_ZONE_BG,
                border: '1px dashed rgba(33, 150, 243, 0.35)',
              }} />
            ))}

          {/* Aisle'lar (yürünebilir) */}
          {data.locations
            .filter(l => l.type === 'Aisle')
            .map(a => (
              <Box key={a.id} sx={{
                position: 'absolute',
                left: a.gridX * cellPx, top: a.gridY * cellPx,
                width: a.gridWidth * cellPx, height: a.gridHeight * cellPx,
                bgcolor: COLOR_AISLE,
                borderRadius: 0.5,
              }} />
            ))}

          {/* Platform'lar */}
          {data.locations
            .filter(l => l.type === 'Platform')
            .map(p => (
              <Box key={p.id} sx={{
                position: 'absolute',
                left: p.gridX * cellPx, top: p.gridY * cellPx,
                width: p.gridWidth * cellPx, height: p.gridHeight * cellPx,
                bgcolor: COLOR_PLATFORM,
                opacity: 0.7,
              }} />
            ))}

          {/* Engeller */}
          {data.obstacles.map(ob => (
            <Box key={ob.id} sx={{
              position: 'absolute',
              left: ob.gridX * cellPx, top: ob.gridY * cellPx,
              width: ob.gridWidth * cellPx, height: ob.gridHeight * cellPx,
              bgcolor: COLOR_OBSTACLE,
              opacity: 0.55,
              borderRadius: 0.5,
            }} />
          ))}

          {/* Raflar */}
          {data.locations
            .filter(l => l.type === 'Rack' || l.type === 'Level' || l.type === 'Bin')
            .map(r => {
              const isTarget = targetItem?.locationId === r.id;
              const isPicked = pickedIds.has(r.id);
              const isRemain = remainIds.has(r.id);
              const fill = isTarget ? COLOR_TARGET
                : isPicked ? COLOR_PICKED
                : isRemain ? COLOR_REMAIN
                : COLOR_OTHER;
              return (
                <Box key={r.id} sx={{
                  position: 'absolute',
                  left: r.gridX * cellPx, top: r.gridY * cellPx,
                  width: (r.gridWidth || 1) * cellPx,
                  height: (r.gridHeight || 1) * cellPx,
                  bgcolor: fill,
                  borderRadius: 0.5,
                  boxShadow: isTarget ? '0 0 0 2px rgba(251,140,0,0.35)' : 'none',
                  animation: isTarget ? 'pickerRackPulse 1.6s ease-in-out infinite' : 'none',
                }} />
              );
            })}

          {/* Entry / Exit */}
          {data.entryExits.map(ee => {
            const isEntry = ee.nodeType === 'Entry';
            const color = isEntry ? '#388e3c' : '#ef6c00';
            return (
              <Box key={ee.id} sx={{
                position: 'absolute',
                left: ee.gridX * cellPx,
                top: ee.gridY * cellPx,
                width: 2 * cellPx,
                height: 2 * cellPx,
                borderRadius: '50%',
                bgcolor: `${color}22`,
                border: `1.5px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {isEntry
                  ? <LoginIcon sx={{ color, fontSize: cellPx * 1.1 }} />
                  : <LogoutIcon sx={{ color, fontSize: cellPx * 1.1 }} />}
              </Box>
            );
          })}

          {/* Rota çizgileri: ghost alternatif (altta) + seçili rota (üstte) */}
          {(ghostRoute.length > 1 || route.length > 1) && (
            <svg
              style={{
                position: 'absolute', left: 0, top: 0,
                width: '100%', height: '100%',
                pointerEvents: 'none', overflow: 'visible',
              }}
            >
              <defs>
                <marker id="picker-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L6,3 z" fill={COLOR_ROUTE} />
                </marker>
              </defs>
              {ghostRoute.length > 1 && (
                <polyline
                  points={ghostRoute.map(p => `${(p.gridX + 0.5) * cellPx},${(p.gridY + 0.5) * cellPx}`).join(' ')}
                  fill="none"
                  stroke={COLOR_GHOST}
                  strokeWidth={Math.max(2, cellPx * 0.16)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={`${cellPx * 0.35} ${cellPx * 0.45}`}
                  opacity={0.85}
                />
              )}
              {route.length > 1 && (
                <>
                  <polyline
                    points={route.map(p => `${(p.gridX + 0.5) * cellPx},${(p.gridY + 0.5) * cellPx}`).join(' ')}
                    fill="none"
                    stroke={COLOR_ROUTE}
                    strokeWidth={Math.max(2, cellPx * 0.18)}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={`${cellPx * 0.6} ${cellPx * 0.4}`}
                    markerEnd="url(#picker-arrow)"
                    style={{ animation: 'pickerRouteDash 1.2s linear infinite' }}
                  />
                  {/* "Buradasınız" işaretçisi */}
                  <circle
                    cx={(route[0].gridX + 0.5) * cellPx}
                    cy={(route[0].gridY + 0.5) * cellPx}
                    r={Math.max(4, cellPx * 0.45)}
                    fill="#1976d2"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                </>
              )}
            </svg>
          )}
        </Box>
      )}

      {/* Animasyon stilleri */}
      <style>{`
        @keyframes pickerRackPulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50%      { transform: scale(1.08); filter: brightness(1.15); }
        }
        @keyframes pickerHerePulse {
          0%   { transform-origin: center; transform: scale(0.6); opacity: 0.9; }
          100% { transform-origin: center; transform: scale(2.6); opacity: 0; }
        }
        @keyframes pickerRouteDash {
          to { stroke-dashoffset: -20; }
        }
      `}</style>
    </Box>

      {/* Legend — haritanın altında, üzerine binmez */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.25,
          py: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Legend dotColor={COLOR_TARGET} label={t('screens.picking.navMap.legendNext')} />
        <Legend dotColor={COLOR_PICKED} label={t('screens.picking.navMap.legendPicked')} />
        <Legend dotColor={COLOR_REMAIN} label={t('screens.picking.navMap.legendRemaining')} />
        <Legend dotColor="#1976d2" label={t('screens.picking.navMap.legendHere')} />
        {ghostRoute.length > 1 && (
          <Legend
            dotColor={COLOR_GHOST}
            label={t('screens.picking.navMap.legendAltRoute', { defaultValue: 'Alternatif rota' })}
          />
        )}
      </Box>

    {footer ? (
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {footer}
      </Box>
    ) : null}
    </Paper>
  );
};

const Legend: React.FC<{ dotColor: string; label: string }> = ({ dotColor, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />
    <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1, color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

export default PickerNavMap;
